import type { IServiceEmploymentSetting } from '../../personalSettings/addRights/utils/serviceEmploymentSettingsUtils';
import { ServiceCode } from '../../../../../common/enums/serviceCode';
import { SpirometerSoftwareNames } from '../../../../../common/enums/spirometerSoftwareNames';
import { translateKey } from '../../../../translate';

export enum SettingAnswerTypes {
    boolean = 'switch',
    canReadAndOrWrite = 'radio',
    dropdown = 'dropdown',
    input = 'input',
    subsequentServices = 'subsequentServices',
}

const sendNotificationsSettingsList = (header: string) => ({
    sendNotificationPractitioner: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsShared.sendNotificationPractitioner'),
        header,
    },
    sendNotificationEmployment: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsShared.sendNotificationEmployment'),
        header,
    },
});

const hardwareSettingsList = (isConsumer: boolean) => ({
    usingSpirometer: {
        type: SettingAnswerTypes.boolean,
        conditionServiceCodes: isConsumer ? [ServiceCode.TPC] : [ServiceCode.TPO],
        label: translateKey('addCarePathSettingsLabelsShared.usingSpirometer'),
        header: translateKey('addCarePathSettingsHeaders.software'),
    },
    spirometerSoftwareName: {
        type: SettingAnswerTypes.dropdown,
        options: Object.keys(SpirometerSoftwareNames),
        conditionKey: 'usingSpirometer',
        conditionServiceCodes: isConsumer ? [ServiceCode.TPC] : [ServiceCode.TPO],
        label: translateKey('addCarePathSettingsLabelsShared.spirometerSoftwareName'),
        header: translateKey('addCarePathSettingsHeaders.software'),
    },
    usingRestEcgMeter: {
        type: SettingAnswerTypes.boolean,
        conditionServiceCodes: isConsumer ? [ServiceCode.TCCi, ServiceCode.TCCh, ServiceCode.TCCr] : [ServiceCode.TCOR],
        label: translateKey('addCarePathSettingsLabelsShared.usingRestECGMeter'),
        header: translateKey('addCarePathSettingsHeaders.software'),
    },
});

const serviceConsumerAutoSubsequentServicesSettingsList = () => ({
    addAutoSubsequentServices: {
        type: SettingAnswerTypes.subsequentServices,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.addAutoSubsequentServices'),
        header: translateKey('addCarePathSettingsHeaders.closeServiceAndSubsequentService'),
    },
    subsequentServiceId: {
        type: SettingAnswerTypes.subsequentServices,
        conditionKey: 'addAutoSubsequentServices',
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.autoSubsequentServices'),
        header: translateKey('addCarePathSettingsHeaders.closeServiceAndSubsequentService'),
    },
    isDeviation: {
        type: SettingAnswerTypes.subsequentServices,
        conditionKey: 'addAutoSubsequentServices',
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.isDeviation'),
        header: translateKey('addCarePathSettingsHeaders.closeServiceAndSubsequentService'),
    },
    providerOrganizationId: {
        type: SettingAnswerTypes.subsequentServices,
        conditionKey: 'autoSubsequentService',
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.providerOrganizationId'),
        header: translateKey('addCarePathSettingsHeaders.closeServiceAndSubsequentService'),
    },
    providerPractitionerId: {
        type: SettingAnswerTypes.subsequentServices,
        conditionKey: 'autoSubsequentService',
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.providerPractitionerId'),
        header: translateKey('addCarePathSettingsHeaders.closeServiceAndSubsequentService'),
    },
});

const serviceConsumerExaminationSettingsList = () => ({
    isRequestCreator: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.isRequestCreator'),
        header: translateKey('addCarePathSettingsHeaders.startRequest'),
    },
    isAutoCloseRequest: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.isAutoCloseRequest'),
        header: translateKey('addCarePathSettingsHeaders.closeServiceAndSubsequentService'),
    },
    isGraderSelector: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.isGraderSelector'),
        header: translateKey('addCarePathSettingsHeaders.selectGrader'),
    },
    ...serviceConsumerAutoSubsequentServicesSettingsList(),
    ...sendNotificationsSettingsList(translateKey('addCarePathSettingsHeaders.zorgMailAndNotifications')),
    sendNotificationPractitionerWhenDeviant: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.sendNotificationPractitionerWhenDeviant'),
        header: translateKey('addCarePathSettingsHeaders.zorgMailAndNotifications'),
    },
    sendZorgMailOnExaminationRequest: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.sendZorgMailOnExaminationRequest'),
        header: translateKey('addCarePathSettingsHeaders.zorgMailAndNotifications'),
    },
    followUpMeasurementGgz: {
        type: SettingAnswerTypes.boolean,
        conditionServiceCodes: [ServiceCode.GGZOembl, ServiceCode.GGZOint],
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.followUpMeasurementGgz'),
        header: translateKey('addCarePathSettingsHeaders.followUpMeasurementGgz'),
    },
    weeksForFollowUpMeasurementGgz: {
        type: SettingAnswerTypes.input,
        conditionKey: 'followUpMeasurementGgz',
        conditionServiceCodes: [ServiceCode.GGZOembl, ServiceCode.GGZOint],
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.sendAfter'),
        header: translateKey('addCarePathSettingsHeaders.followUpMeasurementGgz'),
    },
});

const serviceConsumerConsultationSettingsList = () => ({
    isRequestCreator: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.isRequestCreator'),
        header: translateKey('addCarePathSettingsHeaders.startRequest'),
    },
    isAutoCloseRequest: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.isAutoCloseRequest'),
        header: translateKey('addCarePathSettingsHeaders.closeServiceAndSubsequentService'),
    },
    ...serviceConsumerAutoSubsequentServicesSettingsList(),
    ...sendNotificationsSettingsList(translateKey('addCarePathSettingsHeaders.zorgMailAndNotifications')),
    ...hardwareSettingsList(true),
});

const serviceConsumerReferralSettingsList = () => ({
    isRequestCreator: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.isRequestCreator'),
        header: translateKey('addCarePathSettingsHeaders.startRequest'),
    },
    isAutoCloseRequest: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.isAutoCloseRequest'),
        header: translateKey('addCarePathSettingsHeaders.closeServiceAndSubsequentService'),
    },
    ...serviceConsumerAutoSubsequentServicesSettingsList(),
    ...sendNotificationsSettingsList(translateKey('addCarePathSettingsHeaders.zorgMailAndNotifications')),
    sendZorgMailOnReferralRequest: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.sendZorgMailOnReferralRequest'),
        header: translateKey('addCarePathSettingsHeaders.zorgMailAndNotifications'),
    },
    sendZorgMailOnReferralIntake: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.sendZorgMailOnReferralIntake'),
        header: translateKey('addCarePathSettingsHeaders.zorgMailAndNotifications'),
    },
});

const serviceConsumerReferralSmallSettingsList = () => ({
    ...serviceConsumerAutoSubsequentServicesSettingsList(),
});

const serviceConsumerCounselingSettingsList = () => ({
    ...serviceConsumerAutoSubsequentServicesSettingsList(),
});

const serviceConsumerContactSettingsList = () => ({
    isRequestCreator: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.isRequestCreator'),
        header: translateKey('addCarePathSettingsHeaders.startRequest'),
    },
});

const serviceConsumerOtherSettingsList = () => ({
    isRequestCreator: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceConsumer.isRequestCreator'),
        header: translateKey('addCarePathSettingsHeaders.startRequest'),
    },
});

const serviceProviderCommonSettingsList = () => ({
    ...sendNotificationsSettingsList(translateKey('addCarePathSettingsHeaders.notifications')),
});

const serviceProviderExaminationSettingsList = () => ({
    ...serviceProviderCommonSettingsList(),
    isAnalyst: {
        type: SettingAnswerTypes.boolean,
        conditionServiceCodes: [ServiceCode.TCOH],
        label: translateKey('addCarePathSettingsLabelsServiceProvider.isAnalyst'),
        header: translateKey('addCarePathSettingsHeaders.analysis'),
    },
    isBiometrician: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceProvider.isBiometrician'),
        header: translateKey('addCarePathSettingsHeaders.examinationAndGrading'),
    },
    isGrader: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceProvider.isGrader'),
        header: translateKey('addCarePathSettingsHeaders.examinationAndGrading'),
    },
    isRequestCreator: {
        type: SettingAnswerTypes.boolean,
        conditionKey: 'isBiometrician',
        label: translateKey('addCarePathSettingsLabelsServiceProvider.isRequestCreator'),
        header: translateKey('addCarePathSettingsHeaders.examinationAndGrading'),
    },
    isGraderSelector: {
        type: SettingAnswerTypes.boolean,
        conditionKey: 'isBiometrician',
        label: translateKey('addCarePathSettingsLabelsServiceProvider.isGraderSelector'),
        header: translateKey('addCarePathSettingsHeaders.examinationAndGrading'),
    },
    isServiceConsumerSelector: {
        type: SettingAnswerTypes.boolean,
        conditionKey: 'isRequestCreator',
        label: translateKey('addCarePathSettingsLabelsServiceProvider.isServiceConsumerSelector'),
        header: translateKey('addCarePathSettingsHeaders.selectConsumer'),
    },
    canSelectCareProviderAsServiceConsumer: {
        type: SettingAnswerTypes.boolean,
        conditionKey: 'isServiceConsumerSelector',
        label: translateKey('addCarePathSettingsLabelsServiceProvider.canSelectCareProviderAsServiceConsumer'),
        header: translateKey('addCarePathSettingsHeaders.selectConsumer'),
    },
    isRequestRecipient: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceProvider.isRequestRecipient'),
        header: translateKey('addCarePathSettingsHeaders.receiveRequest'),
    },
    ...hardwareSettingsList(false),
});

const serviceProviderConsultationSettingsList = () => ({
    ...serviceProviderCommonSettingsList(),
    isRequestRecipient: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceProvider.isRequestRecipient'),
        header: translateKey('addCarePathSettingsHeaders.receiveRequest'),
    },
});

const serviceProviderReferralSettingsList = () => ({
    ...serviceProviderCommonSettingsList(),
});

const serviceProviderReferralSmallSettingsList = () => ({
    ...serviceProviderCommonSettingsList(),
});

const serviceProviderCounselingSettingsList = () => ({});

const serviceProviderContactSettingsList = () => ({
    isRequestRecipient: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsServiceProvider.isRequestRecipient'),
        header: translateKey('addCarePathSettingsHeaders.receiveRequest'),
    },
});

const serviceProviderOtherSettingsList = () => ({});

const serviceSupervisorExaminationSettingsList = () => ({
    sendNotificationPractitioner: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsShared.sendNotificationPractitioner'),
        header: translateKey('addCarePathSettingsHeaders.notifications'),
    },
    sendNotificationEmployment: {
        type: SettingAnswerTypes.boolean,
        label: translateKey('addCarePathSettingsLabelsShared.sendNotificationEmployment'),
        header: translateKey('addCarePathSettingsHeaders.notifications'),
    },
});

// This needs to be a function to get the translations right on runtime
export const settingsList: () => Record<string, Record<string, IServiceEmploymentSetting>> = () => ({
    consumer_examination: serviceConsumerExaminationSettingsList(),
    consumer_consultation: serviceConsumerConsultationSettingsList(),
    consumer_referral: serviceConsumerReferralSettingsList(),
    consumer_referralSmall: serviceConsumerReferralSmallSettingsList(),
    consumer_counseling: serviceConsumerCounselingSettingsList(),
    consumer_contact: serviceConsumerContactSettingsList(),
    consumer_other: serviceConsumerOtherSettingsList(),
    provider_examination: serviceProviderExaminationSettingsList(),
    provider_consultation: serviceProviderConsultationSettingsList(),
    provider_referral: serviceProviderReferralSettingsList(),
    provider_referralSmall: serviceProviderReferralSmallSettingsList(),
    provider_counseling: serviceProviderCounselingSettingsList(),
    provider_contact: serviceProviderContactSettingsList(),
    provider_other: serviceProviderOtherSettingsList(),
    supervisor_examination: serviceSupervisorExaminationSettingsList(),
});
